import { Check, ChevronsUpDown, LayoutList } from "lucide-react";
import { DropdownMenu } from "radix-ui";
import { Link } from "react-router";

import { ROUTES, routeTo } from "@/app/routes";
import { ROLE_LABEL, useBrand } from "@/core/brand/brand-context";
import { useMyBrands } from "@/core/brand/my-brands";
import { BrandMark } from "@/ui/brand-mark";
import { cn } from "@/ui/cn";
import { Tip } from "@/ui/tooltip";

import { menuItemClass } from "./AccountMenu";

/**
 * Which kitchen this is and the person's role in it, at the top of the menu. With several brands it opens a list
 * to switch straight to another (like a workspace switcher); with one, it's just a label. Collapsed: the mark,
 * named in a tooltip.
 */
export function BrandSwitcher({
  collapsed = false,
  onNavigate,
}: {
  collapsed?: boolean;
  onNavigate?: () => void;
}) {
  const { brand, role } = useBrand();
  const brands = useMyBrands();
  const all = brands.data ?? [];
  const several = all.length > 1;
  const name = `${brand.name} · ${ROLE_LABEL[role]}`;
  const mark = <BrandMark name={brand.name} logoUrl={brand.logoUrl} />;
  const face = collapsed ? (
    mark
  ) : (
    <>
      {mark}
      <span className="flex min-w-0 grow flex-col">
        <span className="line-clamp-2 text-sm leading-snug font-bold break-words">{brand.name}</span>
        <span className="text-xs text-muted">{ROLE_LABEL[role]}</span>
      </span>
      {several && <ChevronsUpDown className="size-4 text-muted" aria-hidden="true" />}
    </>
  );
  const row = collapsed
    ? "mx-auto flex rounded-[14px]"
    : "flex w-full min-w-0 items-center gap-3 rounded-xl p-2 text-left";

  if (!several) {
    return collapsed ? (
      <Tip label={name}>
        <span role="img" aria-label={name} tabIndex={0} className={row}>
          {mark}
        </span>
      </Tip>
    ) : (
      <div className={row} title={brand.name}>
        {face}
      </div>
    );
  }

  return (
    <DropdownMenu.Root modal={false}>
      <Tip label={name} enabled={collapsed}>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            aria-label={`${name}. Switch brand`}
            title={collapsed ? undefined : brand.name}
            className={cn(row, "transition-colors hover:bg-surface-muted data-[state=open]:bg-surface-muted")}
          >
            {face}
          </button>
        </DropdownMenu.Trigger>
      </Tip>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          side={collapsed ? "right" : "bottom"}
          align="start"
          sideOffset={8}
          className="z-50 max-h-[70dvh] w-72 overflow-y-auto rounded-xl border border-border bg-surface p-1.5 text-ink shadow-xl"
        >
          <DropdownMenu.Label className="px-2.5 pt-1 pb-1.5 text-[11px] font-bold tracking-[0.08em] text-faint uppercase">
            Switch brand
          </DropdownMenu.Label>
          {all.map(({ brand: b, role: r }) => (
            <DropdownMenu.Item key={b.id} asChild>
              <Link
                to={routeTo.brandHome(b.id)}
                onClick={onNavigate}
                aria-current={b.id === brand.id ? "page" : undefined}
                className={cn(menuItemClass, "min-h-12 text-ink no-underline")}
              >
                <BrandMark name={b.name} logoUrl={b.logoUrl} size="sm" />
                <span className="flex min-w-0 grow flex-col">
                  <span className="truncate font-semibold">{b.name}</span>
                  <span className="text-xs font-normal text-muted">{ROLE_LABEL[r]}</span>
                </span>
                {b.id === brand.id && <Check className="text-primary" aria-hidden="true" />}
              </Link>
            </DropdownMenu.Item>
          ))}
          <DropdownMenu.Separator className="my-1.5 h-px bg-border-subtle" />
          <DropdownMenu.Item asChild>
            <Link
              to={ROUTES.start}
              onClick={onNavigate}
              className={cn(menuItemClass, "text-ink no-underline")}
            >
              <LayoutList className="text-muted" aria-hidden="true" />
              All your brands
            </Link>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
